import { useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Mail, MessageCircle, Phone } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import { organization, programs } from '../data/siteData';

export default function Contact() {
  const [searchParams] = useSearchParams();
  const initialProgram = searchParams.get('programa') || 'rehabilitacion';
  const [topic, setTopic] = useState(programs.some((item) => item.id === initialProgram) ? initialProgram : 'rehabilitacion');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const selectedProgram = useMemo(() => programs.find((item) => item.id === topic), [topic]);

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus('sending');
    setError('');
    const data = new FormData(form);
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}api/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program: topic,
          name: data.get('name'),
          email: data.get('email'),
          phone: data.get('phone'),
          message: data.get('message'),
          website: data.get('website'),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) throw new Error(result.message || 'No fue posible enviar tu consulta.');
      setStatus('sent');
      form.reset();
      if (window.gtag) window.gtag('event', 'generate_lead', { program: topic });
    } catch (submitError) {
      setStatus('idle');
      setError(submitError.message || 'No fue posible enviar tu consulta.');
    }
  };

  return (
    <main id="contenido" className="contact-page">
      <SEO
        title="Contacto | Fundación Tony"
        description="Solicita información sobre los programas de Fundación Tony. Te conectamos con el equipo adecuado."
        path="/contacto"
      />
      <section className="contact-hero">
        <div className="contact-hero__shape" />
        <div className="shell contact-hero__grid">
          <div>
            <span className="eyebrow eyebrow--light">Estamos para orientarte</span>
            <h1>Hablemos de la oportunidad que estás buscando.</h1>
            <p>Selecciona el programa de tu interés. Tu consulta llegará directamente al equipo responsable.</p>
          </div>
          <div className="contact-hero__cards">
            <a href={`https://wa.me/${organization.whatsapp}`} target="_blank" rel="noreferrer"><MessageCircle /><span><small>WhatsApp</small>{organization.phone}</span></a>
            <a href="tel:+522299842590"><Phone /><span><small>Atención telefónica</small>Llámanos</span></a>
          </div>
        </div>
      </section>

      <section className="contact-content section">
        <div className="shell contact-grid">
          <Reveal className="contact-form-wrap">
            <div className="contact-form-head">
              <span className="eyebrow eyebrow--blue">Formulario de contacto</span>
              <h2>Cuéntanos cómo podemos ayudarte</h2>
            </div>
            <form className="contact-form" onSubmit={submit}>
              <label className="contact-form__honeypot" aria-hidden="true">
                <span>No llenar este campo</span>
                <input name="website" type="text" tabIndex="-1" autoComplete="off" />
              </label>
              <label className="field field--wide">
                <span>Tema de consulta</span>
                <select value={topic} onChange={(event) => setTopic(event.target.value)}>
                  {programs.map((program) => <option key={program.id} value={program.id}>{program.title}</option>)}
                </select>
              </label>
              <label className="field">
                <span>Nombre completo</span>
                <input name="name" type="text" autoComplete="name" required placeholder="Tu nombre" />
              </label>
              <label className="field">
                <span>Correo electrónico</span>
                <input name="email" type="email" autoComplete="email" required placeholder="correo@ejemplo.com" />
              </label>
              <label className="field field--wide">
                <span>Teléfono</span>
                <input name="phone" type="tel" autoComplete="tel" placeholder="(229) 000 0000" />
              </label>
              <label className="field field--wide">
                <span>Mensaje</span>
                <textarea name="message" required rows="6" placeholder="Describe brevemente tu consulta" />
              </label>
              <div className="contact-form__recipient">
                <Mail /><span>Esta consulta se dirigirá a <strong>{selectedProgram.email}</strong></span>
              </div>
              <button className="button field--wide" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Enviando consulta…' : 'Enviar consulta'} <ArrowRight />
              </button>
              {status === 'sent' && <p className="form-success"><CheckCircle2 /> Tu consulta fue enviada al equipo correspondiente.</p>}
              {error && <p className="form-error" role="alert">{error}</p>}
              <p className="form-note field--wide">Al continuar aceptas que usemos los datos proporcionados únicamente para responder tu consulta.</p>
            </form>
          </Reveal>

          <aside className="contact-sidebar">
            <Reveal>
              <span className="eyebrow eyebrow--blue">Contacto por programa</span>
              <h2>Un canal directo para cada necesidad</h2>
              <p>También puedes escribir al correo del programa correspondiente.</p>
            </Reveal>
            {programs.map((program, index) => {
              const Icon = program.icon;
              return (
                <Reveal className={`contact-program contact-program--${program.theme}`} key={program.id} delay={index * 0.08}>
                  <span><Icon /></span>
                  <div><strong>{program.shortTitle}</strong><a href={`mailto:${program.email}`}>{program.email}</a></div>
                </Reveal>
              );
            })}
          </aside>
        </div>
      </section>
    </main>
  );
}
